import React, { useState, useEffect, useCallback } from 'react';
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/core';
import { RJSFSchema, UiSchema, WidgetProps } from '@rjsf/utils';
import {
  generateUISchema,
  applyReferenceWidgets,
} from './SchemaFormTemplates/generateUISchema';
import HorizontalFieldTemplate from './SchemaFormTemplates/HorizontalFieldTemplate';
import CustomArrayFieldTemplate from './SchemaFormTemplates/CustomArrayFieldTemplate';
import CustomDescriptionField from './SchemaFormTemplates/CustomDescriptionField';
import CustomFieldHelpTemplate from './SchemaFormTemplates/CustomFieldHelpTemplate';
import CustomFieldErrorTemplate from './SchemaFormTemplates/CustomFieldErrorTemplate';
import { nestedErrorSchema } from './SchemaFormTemplates/nestedErrorSchema';
import RowAwareObjectFieldTemplate from './SchemaFormTemplates/RowAwareObjectFieldTemplate';
import DescribedSelectWidget from './SchemaFormTemplates/DescribedSelectWidget';
import { CheckboxSwitchWidget } from './SchemaFormTemplates/CustomCheckbox';
import { useTranslation } from 'react-i18next';
import { DraftReferenceState } from 'api/referenceOptions';

// Define the types for SchemaFormProps
type SchemaFormProps = {
  userIsAdmin: boolean;
  formTitle: string;
  schema: RJSFSchema;
  ui_schema: UiSchema;
  formdata?: Object;
  onChange: Function;
  // Namespaces this form's DOM ids. Screens that mount several forms at once
  // (the Route wizard stacks one per action) must pass it: rjsf defaults every
  // field to `root_<field>`, so without it two sections share field ids and a
  // label can point at the other section's input.
  idPrefix?: string;
  isEditable: boolean;
  errors?: any;
  integrationId?: string;
  connectionName?: string;
  authToken?: string;
  draftReference?: DraftReferenceState;
  // The host draws the section description itself (in the section heading).
  hideRootDescription?: boolean;
};

const SchemaForm = ({
  userIsAdmin,
  formTitle,
  schema,
  ui_schema,
  formdata,
  onChange,
  idPrefix,
  hideRootDescription = false,
  isEditable,
  errors,
  integrationId,
  connectionName,
  authToken,
  draftReference,
}: SchemaFormProps) => {
  const { t } = useTranslation();
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});
  const [customSchema, setCustomSchema] = useState<UiSchema>({});
  const [formData, setFormData] = useState(formdata ? formdata : null);
  const [loading, setLoading] = useState(true); // Add loading state
  const printError = (error: any) =>
    console.error('schema form error', { error });

  // Create a toggle password function that takes the field name as a parameter
  // We use useCallback with empty deps to ensure this function never changes its reference
  const togglePassword = useCallback((fieldName: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  }, []);

  // Store a reference to togglePassword that can be safely used in dependency arrays
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTogglePassword = React.useMemo(() => togglePassword, []);

  // Initial setup and when dependencies change
  useEffect(() => {
    if (formData) {
      setCustomSchema(
        applyReferenceWidgets(
          generateUISchema(
            userIsAdmin,
            schema,
            ui_schema,
            errors,
            visiblePasswords,
            stableTogglePassword,
            formData,
          ),
        ),
      );
    }
  }, [
    userIsAdmin,
    schema,
    ui_schema,
    errors,
    formData,
    visiblePasswords,
    stableTogglePassword,
  ]);

  // Initialize form data from props, auto-selecting single-option enum fields
  useEffect(() => {
    const data: any = { ...(formdata || {}) };
    if (schema?.properties) {
      Object.keys(schema.properties).forEach((key) => {
        const prop = (schema.properties as any)[key];
        if (
          prop?.enum?.length === 1 &&
          (data[key] === undefined || data[key] !== prop.enum[0])
        ) {
          data[key] = prop.enum[0];
        }
      });
    }
    setFormData(data);
  }, [formdata, schema]);

  const handleFormChange = (newFormData: any) => {
    setFormData(newFormData); // Update form data without regenerating UI schema on every change
  };

  return (
    <div
      className="flex w-full flex-col"
      id={idPrefix ? `json-form-${idPrefix}` : 'json-form'}
    >
      {/* Display root level description below the form title if it exists, preferring ui_schema['ui:description'] */}
      {hideRootDescription ? null : ui_schema && ui_schema['ui:description'] ? (
        <div className="form-root-description mb-3 mt-1 text-ui-text-primary-text transition-colors duration-300">
          {ui_schema['ui:description']}
        </div>
      ) : schema.description ? (
        <div className="form-root-description mb-3 mt-1 text-ui-text-primary-text transition-colors duration-300">
          {schema.description}
        </div>
      ) : null}
      <div className="custom-form w-full rounded transition-all duration-300">
        <Form
          key={JSON.stringify(customSchema)}
          // Namespaces every field id below this form — see idPrefix above.
          {...(idPrefix ? { idPrefix } : {})}
          schema={schema}
          uiSchema={{
            ...customSchema,
            // Hide the root level description in the form since we're showing it above
            'ui:description': '',
          }}
          validator={validator}
          onChange={(e, fieldId) => {
            handleFormChange(e.formData);
            // fieldId is set only for user-originated edits; rjsf's initial
            // onChange (default population on mount) omits it.
            onChange(e.formData, fieldId);
          }}
          onError={printError}
          // Fields inside list rows; top-level ones are flagged by generateUISchema.
          extraErrors={nestedErrorSchema(errors)}
          // Each message shows under its own field; no summary list on top.
          showErrorList={false}
          children={true}
          formData={formData}
          // Exposed to custom widgets/fields via props.registry.formContext.
          // rootFormData mirrors the live `formData` state (kept current by
          // handleFormChange on every onChange), not the static `formdata` prop,
          // so widgets always see the current in-progress edits.
          // idPrefix travels with the data: reference widgets turn their field id
          // back into a data path, which they cannot do without knowing the root.
          formContext={{
            rootFormData: formData,
            integrationId,
            connectionName,
            authToken,
            idPrefix,
            draftReference,
          }}
          disabled={!isEditable}
          className="rjsf-form-tailwind transition-all duration-300"
          // Shows per-option descriptions; falls back to rjsf's stock select
          // for fields without them.
          widgets={{
            SelectWidget: DescribedSelectWidget,
            CheckboxWidget: CheckboxSwitchWidget,
          }}
          templates={{
            FieldTemplate: HorizontalFieldTemplate,
            ArrayFieldTemplate: CustomArrayFieldTemplate,
            ObjectFieldTemplate: RowAwareObjectFieldTemplate,
            DescriptionFieldTemplate: CustomDescriptionField,
            FieldHelpTemplate: CustomFieldHelpTemplate,
            FieldErrorTemplate: CustomFieldErrorTemplate,
          }}
        />
      </div>
    </div>
  );
};

export default SchemaForm;
